import type { User } from "firebase/auth";
import { StorageSerializers, useStorage } from "@vueuse/core";

import type { IResponseAtlitUser, IResponseAtlitUserV2, IResponsePaymentUserProfile, IUser } from "~/types";
import formatUser from "~/account/helpers/format-user";
import { useTeamInvitations } from "~/teams/composables/use-team-invitations";

type IUserProfile = IResponsePaymentUserProfile["customer"] & {
  hasOwnTeam: boolean;
};

interface ILoginAsUserState {
  id?: string;
  lastLocation?: string;
  user?: IUser;
}

export function useUser() {
  return useStorage<IUser | null>("user", null, undefined, { serializer: StorageSerializers.object });
}

export function useUserToken() {
  return useStorage<string | null>("token", null, undefined, { serializer: StorageSerializers.string });
}

export function useUserProfile() {
  return useStorage<IUserProfile | null>("user-profile", null, undefined, { serializer: StorageSerializers.object });
}

export function useLoginAsUser() {
  const route = useRoute();

  const state = useStorage<ILoginAsUserState | null>("login-as-user", null, undefined, { serializer: StorageSerializers.object });

  return {
    state: unref(state),
    set: (props: ILoginAsUserState) => {
      state.value = { ...state.value, lastLocation: route.path, ...props };
    },
    clear: () => {
      state.value = null;
    },
  };
}

export function getAtlitUser(): Promise<{ id: string; customData: IResponseAtlitUser["attributes"]["custom"]; tenantId: string; hasTenantAdminRole: boolean; showSendBreakdownsBanner?: boolean;
}> {
  return useAPI<{ data: IResponseAtlitUser }>("/api/v1/users/me").then((res) => ({
    id: res.data.id,
    customData: res.data.attributes.custom,
    tenantId: res.data.attributes.tenantId || "",
    hasTenantAdminRole: res.data.attributes.isTenantAdmin,
    showSendBreakdownsBanner: res.data.attributes.showSendBreakdownsBanner,
  }));
}

export function getAtlitUserV2() {
  return useAPI<{ data: IResponseAtlitUserV2 }>(`/api/v2/users/me`).then((res) => res.data);
}

export function patchAtlitUser(body: { custom: IResponseAtlitUser["attributes"]["custom"] }): Promise<any> {
  return useAPI<{ data: IResponseAtlitUser }>("/api/v1/users/me", {
    method: "PATCH",
    body,
  });
}

export function getUserProfile(): Promise<{ customer: IResponsePaymentUserProfile["customer"]; activeTeam: IResponsePaymentUserProfile["activeTeam"] }> {
  return useAPI<IResponsePaymentUserProfile>("/api/v1/payment/users/profile").then((response) => {
    return {
      customer: response.customer,
      activeTeam: response.activeTeam,
    };
  });
}

export async function updateUser(currentUser: User): Promise<void> {
  const user = useUser();
  const token = useUserToken();
  const loginAsUser = useLoginAsUser();

  const idTokenResult = await currentUser.getIdTokenResult().catch((error) => {
    console.error(error);
    return null;
  });

  if (!idTokenResult) {
    return;
  }

  user.value = loginAsUser.state ? loginAsUser.state.user : formatUser(currentUser, idTokenResult);
  token.value = idTokenResult.token;
}

export function updateUserPhone(phoneNumber: string) {
  return useAPI("/api/v1/payment/users/customer", {
    method: "PUT",
    body: { phone: phoneNumber },
  });
}

export function clearUser(): void {
  const user = useUser();
  const token = useUserToken();
  const segment = useSegment();
  const userProfile = useUserProfile();
  const { isInvitePopupOpened } = useTeamInvitations();

  clearMemoizedCaches();
  clearPersistedQueries();

  segment.reset();
  user.value = null;
  token.value = null;
  userProfile.value = null;
  isInvitePopupOpened.value = false;

  // Reset Zoho session
  // @ts-expect-error TODO: Fix this
  if (typeof window !== "undefined" && window.$zoho && window.$zoho.salesiq && typeof window.$zoho.salesiq.reset === "function") {
    // @ts-expect-error TODO: Fix this
    window.$zoho.salesiq.reset();
  }
}

export function logoutUser(): void {
  const { $firebaseAuth } = useNuxtApp();

  clearUser();

  $firebaseAuth.signOut();
}

export function preloadUserData() {
  const user = useUser();
  const userProfile = useUserProfile();

  const p0 = getAtlitUser().then((atlitUser) => {
    if (user.value) {
      user.value.atlitId = atlitUser.id;
      user.value.customData = atlitUser.customData;
      user.value.tenantId = atlitUser.tenantId;
      user.value.hasTenantAdminRole = atlitUser.hasTenantAdminRole;
      user.value.showSendBreakdownsBanner = atlitUser.showSendBreakdownsBanner;
    }
  });
  const p1 = getUserProfile().then((profileData) => {
    userProfile.value = profileData.customer ? { ...profileData.customer, hasOwnTeam: Boolean(profileData.activeTeam) } : null;

    if (user.value) {
      user.value.ownTeamId = profileData.activeTeam?.id;
    }
  });
  const p2 = getCachedLeaguesList();
  const p3 = getCachedOrganizationsList();
  const p4 = getCachedUserTeamsList({
    limit: 20,
    relationships: "memberships,organizations",
    offset: 0,
  });

  return Promise.all([p0, p1, p2, p3, p4]).catch((err) => console.error(err));
}

export async function loginAsUser(userId: string) {
  const user = useUser();
  const loginAsUser = useLoginAsUser();

  if (!user.value?.hasSuperAdminRole) {
    throw new Error("Only super admins can use login-as functionality");
  }

  await loginAsUser.set({ id: userId });

  await getAtlitUserV2().then(async (response) => {
    await clearMemoizedCaches();

    user.value = {
      atlitId: response.id,
      uid: response.attributes.sub || "",
      email: response.attributes.email || "",
      fullName: response.attributes.name || "",
      tenantId: response.attributes.tenantId || "",
      phoneNumber: null,
      hasEmailVerified: true,
      hasPasswordProvider: false,
      hasB2BAccount: response.attributes.isB2BUser || false,
      hasVidswapAccount: response.attributes.isVsUser || false,
      hasSuperAdminRole: response.attributes.group === "admin" || false,
      hasTenantAdminRole: response.attributes.isTenantAdmin || false,
      hasLeaguesAccess: false,
      hasOrganizationsAccess: false,
      teamsSports: [],
      customData: {},
      showSendBreakdownsBanner: response.attributes.showSendBreakdownsBanner,
    };

    await preloadUserData();
    await loginAsUser.set({ user: user.value });
    reloadNuxtApp({ path: "/" });
  });
}

/**
 * Removes any cache data stored in maps of `useMemoize` based functions
 */
export function clearMemoizedCaches(): void {
  getCachedUserClips.clear();
  getCachedEventGameStatsByIdV1.clear();
  getCachedEventGameStatsByIdV3.clear();
  getCachedEventAthletesStatsById.clear();
  getCachedTeamStatsTypeOptionsByEventId.clear();
  getCachedAthleteStatsTypeOptionsByEventId.clear();
  getCachedEventsList.clear();
  getCachedEventById.clear();
  getCachedFavoriteVideos.clear();
  getCachedLeaguesList.clear();
  getCachedLeagueById.clear();
  getCachedOrganizationsList.clear();
  getCachedUserPlaylists.clear();
  getCachedTeamsList.clear();
  getCachedUserTeamsList.clear();
  getCachedUserTeamsWithInvitationIds.clear();
  getCachedUserTeamById.clear();
  getCachedAthletesList.clear();
  getCachedAthleteById.clear();
  getCachedFeatureFlags.clear();
}

/**
 * Removes any persisted queries from the session storage (see use-route-query-session composable)
 */
function clearPersistedQueries() {
  Object.keys(window.sessionStorage)
    .filter((key) => key.indexOf("query-") === 0)
    .forEach((key) => {
      window.sessionStorage.removeItem(key);
    });
}
